import {
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  HeartIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import { useCallback, useEffect, useRef, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import ManagePartnership from "../../components/institution/modals/ManagePartnership"
import ManageInstitutionTypes from "../../components/institution/modals/ManageInstitution"
import StatisticsCardsInstitution from "../../components/institution/cards/StaticsCards"
import PerformanceStatusOverview from "../../components/institution/cards/PerformanceStatus"
import InstitutionTable from "../../components/institution/InstitutionTable"
import AddInstitutionModal from "../../components/institution/modals/AddInstitution"
import { institutionAPI } from "../../services/api"
import ManageInstitutionTypesModal from "../../components/institution/modals/ManageInstitutionTypeModal"
import toast from "react-hot-toast"

function InstitutionsPage() {
  const queryClient = useQueryClient();
  const [partnershipModal, setPartnershipModal] = useState(false)
  const [institutionModal, setInstitutionModal] = useState(false)
  const [addInstitutionModal, setAddInstitutionModal] = useState(false)
  const [showPerformanceStatus, setShowPerformanceStatus] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
  })
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Fetch statistics using React Query
  const { data: statistics = {} } = useQuery(
    'institutionStatistics',
    async () => {
      const response = await institutionAPI.getDashboard()
      const backendStats = response.data.data.institution_dashboard_view
      return {
        total_institutions: backendStats.total_institutions,
        total_active_institutions: backendStats.total_active_institutions,
        total_inactive_institution: backendStats.total_inactive_institution,
      }
    },
    {
      onError: (error) => {
        console.error("Error fetching statistics:", error)
        toast.error("Failed to load statistics")
      }
    }
  )

  // Fetch institutions using React Query
  const {
    data: institutionsData,
    isLoading
  } = useQuery(
    ['institutions', pagination.currentPage, pagination.perPage],
    async () => {
      const response = await institutionAPI.getInstitutions({
        page: pagination.currentPage,
        per_page: pagination.perPage,
      })
      const { data } = response.data

      return {
        institutions: data.institutions || [],
        meta: data.meta
      }
    },
    {
      onError: (error) => {
        console.error("Error fetching institutions:", error)
        toast.error("Failed to load institutions")
      }
    }
  )

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }))
  }

  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, currentPage: 1 }))
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div className='min-h-screen bg-gray-50 animate-in fade-in duration-1000'>
      <div className='max-w-9xl mx-auto p-2'>
        <div className='flex items-center justify-between mb-8 animate-in slide-in-from-top duration-700'>
          <div className='flex items-center space-x-4'>
            <div className='flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg animate-pulse'>
              <BuildingOfficeIcon className='w-6 h-6 text-blue-600' />
            </div>
            <div>
              <h1 className='text-2xl font-bold text-gray-900'>
                Institution Management
              </h1>
              <p className='text-gray-600'>
                Manage institution with structure, performance, and capacity
                tracking
              </p>
            </div>
          </div>

          <div className='flex items-center space-x-3'>
            <div className='relative inline-block text-left' ref={dropdownRef}>
              <button
                onClick={() => setOpen(!open)}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                style={{ backgroundColor: "#DBEAFE", color: "#1E3A8A" }}
              >
                <HeartIcon className='w-5 h-5' style={{ color: "#2563EB" }} />
                <span className="font-medium text-sm">Manage Partnerships</span>
                <ChevronDownIcon className='w-4 h-4' style={{ color: "#2563EB" }} />
              </button>

              {open && (
                <div className='absolute left-0 mt-2 w-56 origin-top-left bg-white border border-gray-200 rounded-lg shadow-lg z-10'>
                  <div className='py-1'>
                    <button
                      onClick={() => {
                        setPartnershipModal(true)
                        setOpen(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors whitespace-nowrap hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      style={{ backgroundColor: "#DBEAFE", color: "#1E3A8A" }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5 mr-2"
                        style={{ color: "#2563EB" }}
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2" />
                      </svg>
                      <span className="font-medium text-sm">Manage Partnerships</span>
                    </button>
                    <button
                      className='flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap'
                      onClick={() => {
                        setInstitutionModal(true)
                        setOpen(false)
                      }}
                    >
                      <BuildingOffice2Icon className='w-4 h-4 mr-2 text-gray-400' />
                      Manage Institution Types
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modals */}
            {partnershipModal && (
              <ManagePartnership
                isOpen={partnershipModal}
                onClose={() => setPartnershipModal(false)}
              />
            )}
            {institutionModal && (
              <ManageInstitutionTypesModal
                isOpen={institutionModal}
                onClose={() => setInstitutionModal(false)}
              />
            )}
            {addInstitutionModal && (
              <AddInstitutionModal
                isOpen={addInstitutionModal}
                onClose={() => setAddInstitutionModal(false)}
                onSuccess={() => {
                  // Invalidate both queries to trigger refetch
                  queryClient.invalidateQueries('institutions');
                  queryClient.invalidateQueries('institutionStatistics');
                  setAddInstitutionModal(false);
                }}
              />
            )}

            <button
              onClick={() => {
                setAddInstitutionModal(true)
                setOpen(false)
              }}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 hover:scale-105 active:scale-95">
              <PlusIcon className='w-4 h-4 mr-1.5' />
              New Institution
            </button>
          </div>
        </div>

        <StatisticsCardsInstitution statistics={statistics} />

        <PerformanceStatusOverview
          residents={institutionsData?.institutions || []}
          showDetails={showPerformanceStatus}
          toggleDetails={() => setShowPerformanceStatus(!showPerformanceStatus)}
        />

        <InstitutionTable
          data={institutionsData?.institutions || []}
          loading={isLoading}
          onAddInstitution={() => setAddInstitutionModal(true)}
          pagination={{
            ...pagination,
            lastPage: institutionsData?.meta?.last_page || 1,
            total: institutionsData?.meta?.total || 0,
          }}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />

        <div className='mt-4'>
          {/* <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            perPage={pagination.perPage}
            total={pagination.total}
            showSizeSelector={true}
            sizeOptions={[5, 10, 15, 25, 50, 100]}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          /> */}
        </div>
      </div>
    </div>
  )
}

export default InstitutionsPage